import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { EmploymentStatus, PensionContext, PensionParameters } from '../../../contexts/pension-robot/PensionContext';
import { Tooltip } from '../../general/Tooltip';
import { TooltipIndicator } from '../../general/TooltipIndicator';
import { NextButton } from './NavigationButtons';
import { YesNoFormGroup } from '../../general/form-inputs/YesNoFormGroup';
import { AgeInput } from '../../general/AgeInput';
import { CocktailWrapperScreen } from './CocktailWrapperScreen';
import { FormGroup } from '../../general/form-inputs/FormGroup';
import { SelectInput } from '../../general/form-inputs/SelectInput';
import { CurrencyFormGroup } from '../../general/form-inputs/CurrencyFormGroup';

export const PersonalInfoSection: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    return (
        <CocktailWrapperScreen 
            page={1} 
            title={t('pension-cocktail-fill-screen-personal-title')}
        >
            <PersonalInfoInputs/>
        </CocktailWrapperScreen>
    );
};

export const PersonalInfoInputs: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo },
        actions,
    } = useContext(PensionContext);

    return (
        <>
            <AgeInput
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-age'),
                    tooltip: { title: 'TODO', text: 'TODO' },
                }}
                suffix={t('general-year')}
                value={personalInfo?.self?.age}
                onChange={(value = '') => {
                    actions.setParameters({ personalInfo: { self: { age: +value } } });
                }}
                className="mb-8"
            />
            <EmploymentStatusSelectInput
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-employment-status'),
                    tooltip: { title: 'TODO', text: 'TODO' },
                }}
                value={personalInfo?.self?.employmentStatus}
                onChange={(value) => {
                    actions.setParameters({
                        personalInfo: { self: { employmentStatus: value } },
                    });
                }}
                className="mb-8"
            />
            <CurrencyFormGroup
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-salary'),
                    tooltip: { title: 'TODO', text: 'TODO' },
                }}
                value={personalInfo?.self?.salary}
                onChange={(cents) => {
                    actions.setParameters({ personalInfo: { self: { salary: cents } } });
                }}
                className="mb-8"
            />
            <MaritalStatusSelectInput
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-marital-status'),
                    tooltip: { title: 'TODO', text: 'TODO' },
                }}
                value={personalInfo?.maritalStatus}
                onChange={(value) => {
                    actions.setParameters({
                        personalInfo: {
                            maritalStatus: value as PensionParameters['personalInfo']['maritalStatus'],
                        },
                    });
                }}
                className="mb-8"
            />
            {personalInfo?.maritalStatus === 'married' && (
                <>
                    <YesNoFormGroup
                        className="mb-8"
                        title={{
                            label: t('pension-cocktail-fill-screen-personal-question-calculate-together'),
                            tooltip: { title: 'TODO', text: 'TODO' },
                        }}
                        value={
                            personalInfo?.isCalculatedTogether === undefined
                                ? null
                                : personalInfo?.isCalculatedTogether
                                ? 'yes'
                                : 'no'
                        }
                        onChange={(value) => {
                            if (value === 'yes') {
                                actions.setParameters({ personalInfo: { isCalculatedTogether: true } });
                            }
                            if (value === 'no') {
                                actions.setParameters({ personalInfo: { isCalculatedTogether: false } });
                            }
                        }}
                    />
                    {!!personalInfo.isCalculatedTogether && <PartnerInputs />}
                </>
            )}

            <div className="flex flex-row flex-wrap justify-between items-center gap-4 my-8">
                <NextButton nextPage="expenses" isDisabled={!isValid(personalInfo)} />
                <div className="flex items-center">
                    {t('pension-cocktail-fill-screen-personal-beginner-help')}
                    <Tooltip tooltipId="Todo" tooltipTitle="Todo" tooltipText="Todo" className="ml-2">
                        <TooltipIndicator>?</TooltipIndicator>
                    </Tooltip>
                </div>
            </div>
        </>
    );
};

function isValid(parameters: PensionParameters['personalInfo']): boolean {
    const validInfo =
        !!parameters.self?.age &&
        !!parameters.self?.employmentStatus &&
        !!parameters.self?.salary &&
        !!parameters.maritalStatus;
    if (parameters.maritalStatus === 'married') {
        if (parameters.isCalculatedTogether !== false) {
            return (
                validInfo &&
                !!parameters.partner?.age &&
                !!parameters.partner?.employmentStatus &&
                !!parameters.partner?.salary
            );
        }
    }

    return validInfo;
}

const EmploymentStatusSelectInput: React.FC<
    {
        value?: EmploymentStatus;
        onChange: (value: EmploymentStatus) => void;
    } & React.ComponentProps<typeof FormGroup>
> = ({ value, onChange, ...rest }) => {
    const { t } = useTranslation('pension');
    return (
        <FormGroup {...rest}>
            <SelectInput
                onChange={(_value) => onChange(_value as EmploymentStatus)}
                selected={value || null}
                values={[
                    {
                        label: t('pension-cocktail-fill-screen-personal-question-employment-employee'),
                        value: 'employee',
                    },
                    {
                        label: t('pension-cocktail-fill-screen-personal-question-employment-zzp'),
                        value: 'self-employed',
                    },
                    {
                        label: t('pension-cocktail-fill-screen-personal-question-employment-unemployed'),
                        value: 'unemployed',
                    },
                ]}
            />
        </FormGroup>
    );
};

const MaritalStatusSelectInput: React.FC<
    {
        value?: PensionParameters['personalInfo']['maritalStatus'];
        onChange: (value: string) => void;
    } & React.ComponentProps<typeof FormGroup>
> = ({ value, onChange, ...rest }) => {
    const { t } = useTranslation('pension');
    return (
        <FormGroup {...rest}>
            <SelectInput
                values={[
                    {
                        label: t('pension-cocktail-fill-screen-personal-question-marital-status-single'),
                        value: 'single',
                    },
                    { label: t('pension-cocktail-fill-screen-personal-question-marital-status-wed'), value: 'married' },
                ]}
                selected={value || null}
                onChange={onChange}
            />
        </FormGroup>
    );
};

const PartnerInputs: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo },
        actions,
    } = useContext(PensionContext);
    return (
        <>
            <AgeInput
                suffix={t('general-year')}
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-partner-age'),
                    tooltip: { title: 'TODO', text: 'TODO' },
                }}
                value={personalInfo?.partner?.age ? personalInfo?.partner?.age : undefined}
                onChange={(value = '') => {
                    actions.setParameters({ personalInfo: { partner: { age: +value } } });
                }}
                className="mb-8"
            />
            <EmploymentStatusSelectInput
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-partner-employment-status'),
                    tooltip: { title: 'TODO', text: 'TODO' },
                }}
                value={personalInfo?.partner?.employmentStatus}
                onChange={(value) => {
                    actions.setParameters({
                        personalInfo: { partner: { employmentStatus: value } },
                    });
                }}
                className="mb-8"
            />
            <CurrencyFormGroup
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-partner-salary'),
                    tooltip: { title: 'TODO', text: 'TODO' },
                }}
                value={personalInfo?.partner?.salary}
                onChange={(cents) => {
                    actions.setParameters({ personalInfo: { partner: { salary: cents } } });
                }}
                className="mb-8"
            />
        </>
    );
};
